import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, PackageCheck, FileText, X, Wallet, BarChart3, Users } from "lucide-react";
import ConcluirInstalacaoModal from "./ConcluirInstalacaoModal";
import InserirContratoModal, { ProcurarClienteModal } from "./InserirContratoModal";
import RegistarPagamentoModal from "./RegistarPagamentoModal";
import AbrirKommoModal from "./AbrirKommoModal";
import TarefaInternaModal from "./TarefaInternaModal";
import { useAuth } from "@/lib/AuthContext";
import { ClipboardList } from "lucide-react";

/**
 * FAB — floating action button visível principalmente em mobile/tablet.
 * Desktop: escondido (fica o "+ Nova venda" que já existe nas páginas).
 * 6 ações: Nova venda, Tarefa interna, Concluir instalação, Inserir contrato, Registar pagamento (admin), Abrir Kommo.
 */
export default function Fab() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [concluirOpen, setConcluirOpen] = useState(false);
    const [contratoOpen, setContratoOpen] = useState(false);
    const [kommoOpen, setKommoOpen] = useState(false);
    const [pagamentoOpen, setPagamentoOpen] = useState(false);
    const [tarefaOpen, setTarefaOpen] = useState(false);
    const [procurarOpen, setProcurarOpen] = useState(false);
    const { user } = useAuth();
    const posVenda = user?.perfil === "posvenda";
    const isAdmin = user?.role === "admin";

    const KIcon = () => (
        <span className="text-[11px] font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>K</span>
    );

    const actions = [
        ...(posVenda ? [] : [{ icon: Plus, label: "Nova venda", color: "var(--v2-primary)", onClick: () => { setOpen(false); navigate("/nova-venda"); }, testid: "fab-nova" }]),
        { icon: ClipboardList, label: "Tarefa interna", color: "#7c3aed", onClick: () => { setOpen(false); setTarefaOpen(true); }, testid: "fab-tarefa" },
        { icon: PackageCheck, label: "Concluir instalação", color: "var(--v2-success)", onClick: () => { setOpen(false); setConcluirOpen(true); }, testid: "fab-concluir" },
        { icon: FileText, label: "Inserir contrato", color: "var(--v2-warning)", onClick: () => { setOpen(false); setContratoOpen(true); }, testid: "fab-contrato" },
        ...(isAdmin ? [{ icon: Wallet, label: "Registar pagamento", color: "#10b981", onClick: () => { setOpen(false); setPagamentoOpen(true); }, testid: "fab-pagamento" }] : []),
        { icon: KIcon, label: "Abrir Kommo", color: "var(--v2-primary)", onClick: () => { setOpen(false); setKommoOpen(true); }, testid: "fab-kommo" },
        ...(posVenda ? [] : [{ icon: BarChart3, label: "Produção", color: "#0ea5e9", onClick: () => { setOpen(false); navigate("/producao"); }, testid: "fab-producao" }]),
        { icon: Users, label: "Procurar cliente", color: "#64748b", onClick: () => { setOpen(false); setProcurarOpen(true); }, testid: "fab-procurar" },
    ];

    return (
        <>
            <div
                className="fixed z-40"
                style={{
                    right: "max(1rem, env(safe-area-inset-right))",
                    bottom: "max(1rem, env(safe-area-inset-bottom))",
                }}
            >
                <AnimatePresence>
                    {open && (
                        <>
                            <motion.div
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                className="fixed inset-0 -z-10"
                                style={{ background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)" }}
                                onClick={() => setOpen(false)}
                            />
                            <motion.div
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                                transition={{ type: "spring", damping: 22, stiffness: 260 }}
                                className="absolute bottom-16 right-0 w-[min(92vw,22rem)] p-2 rounded-2xl shadow-2xl border grid grid-cols-3 sm:grid-cols-4 gap-1.5"
                                style={{ background: "var(--v2-surface)", borderColor: "var(--v2-border)" }}
                                data-testid="fab-menu"
                            >
                                {actions.map((a, i) => (
                                    <motion.button
                                        key={a.label}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: i * 0.03 }}
                                        onClick={a.onClick}
                                        className="flex flex-col items-center justify-center gap-1 h-[4.5rem] rounded-xl text-[10px] font-semibold leading-tight text-center px-1 hover:bg-[color:var(--v2-surface-hover)]"
                                        style={{ color: "var(--v2-foreground)" }}
                                        data-testid={a.testid}
                                    >
                                        <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${a.color}20`, color: a.color }}>
                                            <a.icon size={17} />
                                        </span>
                                        {a.label}
                                    </motion.button>
                                ))}
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>

                <motion.button
                    whileTap={{ scale: 0.95 }}
                    animate={{ rotate: open ? 45 : 0 }}
                    onClick={() => setOpen((v) => !v)}
                    className="h-14 w-14 rounded-full shadow-xl flex items-center justify-center text-white"
                    style={{ background: "var(--v2-primary)" }}
                    data-testid="v2-fab"
                    aria-label="Ações rápidas"
                >
                    {open ? <X size={22} /> : <Plus size={24} />}
                </motion.button>
            </div>

            {concluirOpen && <ConcluirInstalacaoModal onClose={() => setConcluirOpen(false)} />}
            {contratoOpen && <InserirContratoModal onClose={() => setContratoOpen(false)} />}
            {kommoOpen && <AbrirKommoModal onClose={() => setKommoOpen(false)} />}
            {pagamentoOpen && isAdmin && <RegistarPagamentoModal onClose={() => setPagamentoOpen(false)} />}
            {tarefaOpen && <TarefaInternaModal onClose={() => setTarefaOpen(false)} />}
            {procurarOpen && <ProcurarClienteModal onClose={() => setProcurarOpen(false)} />}
        </>
    );
}
